import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media-query and re-render when it flips. Returns true
 * when the query currently matches.
 *
 * SSR-safe: returns `false` until the first effect runs in the browser.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Older Safari fallback
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);

  return matches;
}

/**
 * Tailwind-aligned breakpoint shortcuts.
 *   `useIsMobile()`  ← <640px  (mobile, sidebar hidden, bottom-tab nav)
 *   `useIsTablet()`  ← 640-1023px (collapsed sidebar)
 *   `useIsDesktop()` ← ≥1024px (full sidebar)
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 639.98px)');
}
export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 640px) and (max-width: 1023.98px)');
}
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}
